<template>
  <RsModal
    :value="showEventModal"
    :title="eventInView.name"
    size="lg"
    is-centered
    :show-main-footer="false"
    stop-mousedown-propagation
    @input="handleEventModalClose"
  >
    <!--
      stop-mousedown-propagation: without it, interacting with this modal (e.g. its close
      button) reaches FullCalendar's own document-level listener that auto-closes its
      "+more" popover on an outside mousedown.
    -->
    <template #default>
      <div class="row">
        <div
          class="col-6"
        >
          <div
            v-if="eventInView.start"
            class="font-weight-bold"
          >
            {{ formatEventDay(eventInView.start, eventInView.end) }}
          </div>

          <div class="font-size-14">
            <span v-if="eventInView.start">{{ formatEventTime(eventInView.start) }}</span>
            <span v-if="eventInView.start && eventInView.end"> - </span>
            <span v-if="eventInView.end">{{ formatEventTime(eventInView.end) }}</span>
          </div>
        </div>
        <div class="col-6 text-right">
          <RsButton
            v-if="eventInView.isEditable"
            :class="isIFrame ? 'mr-1' : ''"
            icon="envelope"
            variant="secondary"
            @click="openEmailApp"
          />
          <RsButton
            v-if="eventInView.canOpenLocalCalendar && isButtonVisible('update') && !isIFrame"
            class="mx-1"
            icon="pencil"
            variant="secondary"
            @click="editEventBlock"
          />
          <RsButton
            v-if="eventInView.isDeletable && isButtonVisible('delete')"
            icon="trash-alt"
            variant="error"
            @click="deleteEventBlock"
          />
        </div>
      </div>
      <div
        v-if="eventInView.eventCustomDetails"
      >
        <hr class="event-modal__divider">
        <slot
          name="event-details"
          :details="eventInView.eventCustomDetails"
        />
      </div>
    </template>
  </RsModal>
</template>
<script lang="ts">
import dayjs from 'dayjs';
import { defineComponent, PropType } from 'vue';
import { isIFrame, TAccessType } from '@/shared';
import { ICalendarEvent } from '@/shared/interfaces/calendar-event';

export default defineComponent({
  name: 'RsCalendarEventModal',
  props: {
    /**
     * Event details used to display as event block object in the
     * calendar
     */
    eventInView: {
      type: Object as PropType<ICalendarEvent>,
      default: () => ({}),
    },
    /**
     * Boolean to show/hide selected event modal
     */
    showEventModal: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['on-delete-event-block', 'on-edit-event-block', 'on-modal-close', 'on-show-event-modal'],

  computed: {
    isIFrame(): boolean {
      return this.$rsGlobals.$isIFrame;
    },
  },
  watch: {
    showEventModal(newVal, _oldVal) {
      if (newVal) this.$emit('on-show-event-modal', this.eventInView);
    },
  },
  methods: {
    isButtonVisible(accessType: TAccessType | undefined): boolean {
      return ['tour_admins', 'tour_agents'].some((p) => this.$rsGlobals.$currentUser.hasAtLeastOneModel(p, accessType));
    },
    formatEventDay(startDate: string | Date, endDate: string | Date | undefined): string {
      if (dayjs(`${startDate}`).isSame(dayjs(`${endDate}`), 'day') || !endDate) {
        return dayjs(`${startDate}`).format('dddd, MMMM D');
      }
      return `${dayjs(`${startDate}`).format('dddd, MMMM D')} - ${dayjs(`${endDate}`).format('dddd, MMMM D')}`;
    },
    formatEventTime(date: string | Date): string {
      return dayjs(`${date}`).format('h:mm a');
    },
    handleEventModalClose(): void {
      this.$emit('on-modal-close');
    },
    openEmailApp(): void {
      window.location.href = `mailto:${this.eventInView?.eventCustomDetails?.email}?subject=${this.eventInView?.eventCustomDetails?.propertyName} Tour showing`;
    },
    editEventBlock(): void {
      this.$emit('on-edit-event-block', this.eventInView);
    },
    deleteEventBlock(): void {
      this.$emit('on-delete-event-block', this.eventInView);
    },
  },

});
</script>
<style lang="scss" scoped>
@import '@/styles/_variables.scss';
.event-modal {
    &__divider {
        height: 1px;
    }
}
.font-size-14 {
    font-size: 14px;
}
:deep(footer) {
    display:none!important;
}
</style>
